import type { DeliveryVo, File } from "@zhihaoscm/entity";
import { INFO_LABEL_WIDTH } from "@zhihaoscm/utils";
import { Fragment, useEffect, useMemo, useState } from "react";
import { BaseInfo, BaseItem } from "zhihao-ui-react";
import { fileService } from "@/api/fileService";
import { MediaPreview } from "@/components/media";

const isVideo = (file?: File) => [".mp4", ".mov", ".mkv"].includes(String(file?.type ?? "").toLowerCase());

/** 逐项迁移自旧 delivery/components/proveMaterial.vue。 */
export function DeliveryProofMaterials({ data, embedded = false }: { data: DeliveryVo; embedded?: boolean }) {
    const [files, setFiles] = useState<File[]>([]);
    const evidence = data.delivery.evidenceMaterialInfo;
    const lazb = data.transportOrderDetailsShip?.lazbInfo;
    const ids = useMemo(() => [
        evidence?.tonProveFileId,
        evidence?.sandSourceProofFileId,
        evidence?.loadingPortFileId,
        evidence?.clearanceSaleFileId,
        evidence?.sixWaterGaugesOneFileId || lazb?.sixWaterGaugesOneFileId,
        evidence?.sixWaterGaugesTwoFileId || lazb?.sixWaterGaugesTwoFileId,
        evidence?.sixWaterGaugesThreeFileId || lazb?.sixWaterGaugesThreeFileId,
        evidence?.sixWaterGaugesFourFileId || lazb?.sixWaterGaugesFourFileId,
        evidence?.sixWaterGaugesFiveFileId || lazb?.sixWaterGaugesFiveFileId,
        evidence?.sixWaterGaugesSixFileId || lazb?.sixWaterGaugesSixFileId,
        ...(evidence?.otherMaterialsFileIds ?? [])
    ].filter((value): value is number => Boolean(value)), [evidence, lazb]);
    useEffect(() => {
        if (!ids.length) { setFiles([]); return; }
        void fileService.getFileByIds(ids).then(setFiles).catch(() => setFiles([]));
    }, [ids.join(",")]);
    const find = (id?: number) => files.find(file => file.id === id);
    const primary = [
        { label: "吨位证明", id: evidence?.tonProveFileId },
        { label: "砂石来源证明", id: evidence?.sandSourceProofFileId },
        { label: "到装货港图片/视频", id: evidence?.loadingPortFileId },
        { label: "清仓图片/视频", id: evidence?.clearanceSaleFileId },
        { label: "六处水尺-1", id: evidence?.sixWaterGaugesOneFileId || lazb?.sixWaterGaugesOneFileId },
        { label: "六处水尺-2", id: evidence?.sixWaterGaugesTwoFileId || lazb?.sixWaterGaugesTwoFileId },
        { label: "六处水尺-3", id: evidence?.sixWaterGaugesThreeFileId || lazb?.sixWaterGaugesThreeFileId },
        { label: "六处水尺-4", id: evidence?.sixWaterGaugesFourFileId || lazb?.sixWaterGaugesFourFileId },
        { label: "六处水尺-5", id: evidence?.sixWaterGaugesFiveFileId || lazb?.sixWaterGaugesFiveFileId },
        { label: "六处水尺-6", id: evidence?.sixWaterGaugesSixFileId || lazb?.sixWaterGaugesSixFileId }
    ];
    const otherFiles = files.filter(file => evidence?.otherMaterialsFileIds?.includes(file.id));
    return (
        <section className={`section-card delivery-detail-section${embedded ? " delivery-detail-section--embedded" : ""}`} id="delivery-proof">
            {embedded ? null : <h2 className="section-title">佐证材料</h2>}
            <BaseInfo className="delivery-evidence-grid" cols={4} gap={16} labelWidth={INFO_LABEL_WIDTH.WORD5}>
                {primary.map((item, index) => {
                    const file = find(item.id);
                    return <Fragment key={item.label}><div className={index === 7 ? "delivery-evidence-grid__new-row" : undefined}><MediaPreview height={135} label={item.label} tip="暂无材料" type={isVideo(file) ? "video" : "image"} url={file?.path} width={240} /></div>{index === 6 ? <div aria-hidden="true" className="delivery-evidence-placeholder" /> : null}</Fragment>;
                })}
                <BaseItem infoDirection="vertical" label="其他材料" span={4} valueNode={<div className="delivery-other-materials">{otherFiles.length ? otherFiles.map(file => <MediaPreview height={135} key={file.id} type={isVideo(file) ? "video" : "image"} url={file.path} width={240} />) : <MediaPreview height={135} tip="暂无图片" width={240} />}</div>} />
            </BaseInfo>
        </section>
    );
}
